import React, { useEffect, useState } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { Wallet, Activity, DollarSign, Save, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const chf = (n) => 'CHF ' + (Number(n) || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nf = (n) => (Number(n) || 0).toLocaleString('de-CH');

export default function AiBudgetAdminPage() {
  const [data, setData] = useState(null);
  const [prices, setPrices] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [ov, pr, tf, rv] = await Promise.all([
        superAdminApi.aiOverview(),
        superAdminApi.aiPrices(),
        superAdminApi.aiTariffs(),
        superAdminApi.aiRevenue(),
      ]);
      setData(ov.data);
      setPrices(pr.data.prices || []);
      setTariffs(tf.data.tariffs || []);
      setRevenue(rv.data);
    } catch (e) {
      toast.error('Daten konnten nicht geladen werden');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveAllowance = async (t) => {
    const e = edits[t.slug] || {};
    const body = {};
    if (e.tariff !== undefined) body.ai_tariff = e.tariff || null;
    if (e.included !== undefined && e.included !== '') body.ai_monthly_included_chf = Number(e.included);
    if (Object.keys(body).length === 0) { toast.message('Keine Änderung'); return; }
    setSaving(t.slug);
    try {
      await superAdminApi.aiSetAllowance(t.slug, body);
      toast.success(`Kontingent für ${t.slug} aktualisiert`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const savePrice = async (p) => {
    setSaving('price-' + p.model);
    try {
      await superAdminApi.aiSetPrice({ model: p.model, in_chf_per_m: Number(p.in_chf_per_m), out_chf_per_m: Number(p.out_chf_per_m) });
      toast.success(`Preis für ${p.model} gespeichert`);
    } catch (e) { toast.error('Speichern fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  const saveTariff = async (tf) => {
    setSaving('tariff-' + tf.name);
    try {
      await superAdminApi.aiSetTariff({ name: tf.name, monthly_included_chf: Number(tf.monthly_included_chf) });
      toast.success(`Tarif ${tf.name} gespeichert`);
    } catch (e) { toast.error('Speichern fehlgeschlagen'); }
    finally { setSaving(''); }
  };

  if (loading) return <div className="text-slate-600" data-testid="sa-aibudget-loading">Lädt…</div>;
  if (!data) return <div className="text-rose-600">Keine Daten.</div>;

  return (
    <div data-testid="sa-aibudget" className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">KI-Budget &amp; Guthaben</h1>
        <p className="mt-1 text-sm text-slate-600">Verbrauch, Guthaben und Umsatz je Mandant. Kontingente pro Tarif oder individuell festlegen.</p>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-700"><Activity className="h-5 w-5" /></div>
          <div className="text-3xl font-semibold text-slate-900">{data.totals.tenants}</div>
          <div className="text-sm text-slate-600">Mandanten</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-700"><Wallet className="h-5 w-5" /></div>
          <div className="text-3xl font-semibold text-slate-900">{chf(data.totals.consumed_chf)}</div>
          <div className="text-sm text-slate-600">Verbraucht (Monat)</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-700"><Wallet className="h-5 w-5" /></div>
          <div className="text-3xl font-semibold text-slate-900">{chf(data.totals.extra_credit_chf)}</div>
          <div className="text-sm text-slate-600">Zusatzguthaben gesamt</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-green-500/15 text-green-700"><DollarSign className="h-5 w-5" /></div>
          <div className="text-3xl font-semibold text-slate-900">{chf(data.totals.revenue_chf)}</div>
          <div className="text-sm text-slate-600">Umsatz KI-Guthaben</div>
        </div>
      </div>

      {/* Tenants */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-medium text-slate-900">Mandanten</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="pb-3 font-medium">Mandant</th>
                <th className="pb-3 text-right font-medium">Verbr. / Inkl.</th>
                <th className="pb-3 text-right font-medium">Guthaben</th>
                <th className="pb-3 text-right font-medium">Anfragen</th>
                <th className="pb-3 text-center font-medium">Status</th>
                <th className="pb-3 font-medium">Tarif</th>
                <th className="pb-3 font-medium">Override CHF</th>
                <th className="pb-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {data.tenants.map((t) => {
                const e = edits[t.slug] || {};
                return (
                  <tr key={t.slug} data-testid={`sa-aibudget-row-${t.slug}`}>
                    <td className="py-3 text-slate-800">{t.name || t.slug}<div className="text-xs text-slate-500">{t.slug}</div></td>
                    <td className="py-3 text-right text-slate-700">{chf(t.consumed_chf)} / {chf(t.included_chf)}<div className="text-xs text-slate-500">{Math.round(t.pct_used || 0)}%</div></td>
                    <td className="py-3 text-right text-emerald-700">{chf(t.extra_credit_chf)}</td>
                    <td className="py-3 text-right text-slate-600">{nf(t.requests)}</td>
                    <td className="py-3 text-center">{t.paused ? <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-xs text-red-700"><AlertTriangle className="h-3 w-3" /> Pausiert</span> : <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-700">Aktiv</span>}</td>
                    <td className="py-3">
                      <select
                        className="rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800"
                        defaultValue=""
                        onChange={(ev) => setEdits({ ...edits, [t.slug]: { ...e, tariff: ev.target.value } })}
                        data-testid={`sa-aibudget-tariff-${t.slug}`}
                      >
                        <option value="">— wählen —</option>
                        {tariffs.map((tf) => <option key={tf.name} value={tf.name}>{tf.name} ({tf.monthly_included_chf})</option>)}
                      </select>
                    </td>
                    <td className="py-3">
                      <input
                        type="number"
                        placeholder={String(t.included_chf)}
                        className="w-24 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800"
                        value={e.included ?? ''}
                        onChange={(ev) => setEdits({ ...edits, [t.slug]: { ...e, included: ev.target.value } })}
                        data-testid={`sa-aibudget-included-${t.slug}`}
                      />
                    </td>
                    <td className="py-3 text-right">
                      <button onClick={() => saveAllowance(t)} disabled={saving === t.slug} className="inline-flex items-center gap-1 rounded-lg bg-indigo-500/20 px-3 py-1.5 text-xs text-indigo-700 hover:bg-indigo-500/30 disabled:opacity-50" data-testid={`sa-aibudget-save-${t.slug}`}>
                        {saving === t.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Speichern
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tariffs */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-medium text-slate-900">Tarif-Standardkontingente (CHF / Monat)</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {tariffs.map((tf, i) => (
            <div key={tf.name} className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`sa-aibudget-tariff-card-${tf.name}`}>
              <div className="mb-2 text-sm font-medium text-slate-800">{tf.name}</div>
              <div className="flex items-center gap-2">
                <input type="number" className="w-24 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800"
                  value={tf.monthly_included_chf}
                  onChange={(ev) => { const c = [...tariffs]; c[i] = { ...tf, monthly_included_chf: ev.target.value }; setTariffs(c); }}
                  data-testid={`sa-aibudget-tariff-input-${tf.name}`} />
                <button onClick={() => saveTariff(tariffs[i])} className="rounded-lg bg-indigo-500/20 px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-500/30" data-testid={`sa-aibudget-tariff-save-${tf.name}`}><Save className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Model prices */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="mb-1 text-lg font-medium text-slate-900">Modell-Preise (CHF pro 1 Mio. Tokens)</h2>
        <p className="mb-4 text-xs text-slate-500">Basis für die interne Kostenberechnung je KI-Anfrage. Eingang = Prompt-Tokens, Ausgang = Antwort-Tokens.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500"><th className="pb-3">Modell</th><th className="pb-3">Eingang</th><th className="pb-3">Ausgang</th><th className="pb-3"></th></tr></thead>
            <tbody className="divide-y divide-slate-200">
              {prices.map((p, i) => (
                <tr key={p.model} data-testid={`sa-aibudget-price-${p.model}`}>
                  <td className="py-2 text-slate-800">{p.model}</td>
                  <td className="py-2"><input type="number" step="0.01" className="w-24 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800" value={p.in_chf_per_m} onChange={(ev) => { const c = [...prices]; c[i] = { ...p, in_chf_per_m: ev.target.value }; setPrices(c); }} data-testid={`sa-aibudget-price-in-${p.model}`} /></td>
                  <td className="py-2"><input type="number" step="0.01" className="w-24 rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-slate-800" value={p.out_chf_per_m} onChange={(ev) => { const c = [...prices]; c[i] = { ...p, out_chf_per_m: ev.target.value }; setPrices(c); }} data-testid={`sa-aibudget-price-out-${p.model}`} /></td>
                  <td className="py-2"><button onClick={() => savePrice(prices[i])} className="rounded-lg bg-indigo-500/20 px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-500/30" data-testid={`sa-aibudget-price-save-${p.model}`}><Save className="h-3.5 w-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Revenue */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sa-aibudget-revenue">
        <h2 className="mb-4 text-lg font-medium text-slate-900">Umsatz-Log (KI-Guthaben)</h2>
        {(!revenue?.transactions || revenue.transactions.length === 0) ? (
          <p className="text-sm text-slate-500">Noch keine Zahlungen.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500"><th className="pb-3">Datum</th><th className="pb-3">Mandant</th><th className="pb-3">Typ</th><th className="pb-3 text-right">Betrag</th></tr></thead>
            <tbody className="divide-y divide-slate-200">
              {revenue.transactions.slice(0, 50).map((r, i) => (
                <tr key={r.session_id || i}>
                  <td className="py-2 text-slate-600">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="py-2 text-slate-700">{r.tenant_slug}</td>
                  <td className="py-2 text-slate-600">{r.type}</td>
                  <td className="py-2 text-right text-green-700">{chf(r.amount_chf)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
